import React, { useState, useEffect, useId } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { useTheme } from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useStatsigClient } from '@statsig/react-bindings'
import Marquee from 'react-fast-marquee'
import {
  useMobileMediaQuery,
  useDesktopMediaQuery
} from 'utils/hooks/useMediaQueries'
import { dateHelpers, stringHelpers } from 'utils'
import PlayerActions from 'reducers/Player'
import {
  PlaybarWrapper,
  CoverArt,
  ReleaseWrapper,
  ReleaseArtist,
  ReleaseTitle,
  ReleaseDetailsWrapper,
  OptionsWrapper,
  IconWrapper,
  PlayerDetailsWrapper,
  PlayOptionsWrapper,
  VolumeSliderWrapper,
  PlayerIconWrapper,
  DurationWrapper,
  Wrap,
  SliderWrapper,
  SkipPreviousIcon,
  SkipNextIcon,
  IconContainer,
  ToggleContainer
} from './Playbar.styles'
import { VolumeSlider, Toggle, Tonality } from '@dsny/dsny-component-library'
import ExpandedPlaybar from './ExpandedPlaybar'
import { albumCovertArtPlaceholder } from 'images'
import {
  Info,
  PlayArrowFilled,
  PauseFilled,
  SkipPreviousFilled,
  SkipNextFilled
} from '@dsny/dsny-component-library/icons'
import Slider from 'rc-slider'
import TrackPlaylistDropdown from '../Dropdowns/PlaylistDropdown/TrackPlaylistDropdown'
import usePlayerControls from './usePlayerControls'
import { AUTOPLAY_TOGGLE_TO_QUEUE } from 'constants/featureFlags'

const createSliderWithTooltip = Slider.createSliderWithTooltip
const SliderWithTooltip = createSliderWithTooltip(Slider)

const Playbar = () => {
  const autoPlayToggleId = useId()
  const theme = useTheme()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const isMobile = useMobileMediaQuery()
  const isDesktop = useDesktopMediaQuery()
  const { checkGate, isLoading: isStatsigLoading } = useStatsigClient()
  const {
    isPlaying,
    currentTime,
    displayDuration,
    selectedSong,
    localVolume,
    isMuted,
    isSliderDragging,
    isFirstSong,
    isLastSong,
    handleTogglePlay,
    handleSkipBack,
    handleSkipFwd,
    handleVolumeChange,
    handleVolumeIconClick,
    hasNoSong,
    setIsSliderDragging,
    autoPlay,
    handleToggleAutoPlay
  } = usePlayerControls()
  const isAutoplayGateReady = !isMobile && !isStatsigLoading
  const isAutoplayToggleInQueue =
    isAutoplayGateReady && checkGate(AUTOPLAY_TOGGLE_TO_QUEUE)

  const [isPlaybarExpanded, setIsPlaybarExpanded] = useState(false)
  const [sliderCurrentTime, setSliderCurrentTime] = useState(currentTime)
  const [isHovered, setIsHovered] = useState(false)
  const { coverart, title, artist } = selectedSong

  useEffect(() => {
    if (!isSliderDragging) {
      setSliderCurrentTime(currentTime)
    }
  }, [currentTime, isSliderDragging])

  const onInfoReleaseClick = (e) => {
    if (hasNoSong) return
    e.stopPropagation()
    navigate(`/releases/releaseInfo/${selectedSong.releaseid}`)
  }

  const togglePlaybar = () => {
    setIsPlaybarExpanded(!isPlaybarExpanded)
  }

  const handleSliderRelease = (value) => {
    dispatch(PlayerActions.seekTimeRequest(value))
    setSliderCurrentTime(value)
    setIsSliderDragging(false)
  }

  const handleSliderChange = (value) => {
    setSliderCurrentTime(value)
    setIsSliderDragging(true)
  }

  const getSliderTip = (value) => {
    const displayTime = Math.min(value, displayDuration)
    return dateHelpers.getStringFromSeconds(displayTime)
  }

  const renderTitle = (title) => {
    const maxLength = isDesktop ? 90 : isMobile ? 35 : 25
    const marqueeWidth = isMobile ? (isDesktop ? '60%' : '90%') : '45%'

    return title?.length > maxLength ? (
      <Marquee style={{ width: marqueeWidth }} pauseOnHover speed={20}>
        <ReleaseTitle>{title}</ReleaseTitle>
      </Marquee>
    ) : (
      <ReleaseTitle>{title}</ReleaseTitle>
    )
  }
  const renderArtist = (artist) => {
    const maxLength = isDesktop ? 90 : isMobile ? 35 : 25
    const marqueeWidth = isMobile ? (isDesktop ? '60%' : '90%') : '45%'

    return artist?.length > maxLength ? (
      <Marquee style={{ width: marqueeWidth }} pauseOnHover speed={20}>
        <ReleaseArtist>{artist}</ReleaseArtist>
      </Marquee>
    ) : (
      <ReleaseArtist>{artist}</ReleaseArtist>
    )
  }

  return (
    <>
      <SliderWrapper
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <SliderWithTooltip
          value={isSliderDragging ? sliderCurrentTime : currentTime}
          defaultValue={0}
          min={0}
          max={displayDuration}
          step={0.01}
          className="cursor-pointer"
          style={{ height: 12, padding: '8px 0 0' }}
          trackStyle={{
            backgroundColor: isHovered
              ? theme.colors.player50
              : theme.colors.neutralW100
          }}
          railStyle={{
            backgroundColor: theme.colors.neutralW40
          }}
          handleStyle={{
            borderColor: isHovered ? theme.colors.neutralW100 : 'transparent',
            width: isHovered ? 12 : 0,
            height: isHovered ? 12 : 0,
            backgroundColor: isHovered
              ? theme.colors.neutralW100
              : 'transparent'
          }}
          tipFormatter={getSliderTip}
          onChange={handleSliderChange}
          onAfterChange={handleSliderRelease}
        />
      </SliderWrapper>
      <PlaybarWrapper
        data-playbar-root="true"
        onClick={isMobile ? togglePlaybar : null}
        $isDesktop={isDesktop}
      >
        <ReleaseDetailsWrapper>
          <CoverArt
            $isMobile={isMobile}
            src={coverart ? coverart : albumCovertArtPlaceholder}
            alt="Cover art"
          />
          <ReleaseWrapper>
            {renderTitle(hasNoSong ? '' : title)}
            {renderArtist(hasNoSong ? '' : artist)}
          </ReleaseWrapper>
        </ReleaseDetailsWrapper>
        {isMobile ? (
          <PlayOptionsWrapper>
            <IconContainer>
              <PlayerIconWrapper $hasNoSong={hasNoSong}>
                {isPlaying && !hasNoSong ? (
                  <PauseFilled
                    width={24}
                    color={theme.colors.neutralW20}
                    onClick={handleTogglePlay}
                  />
                ) : (
                  <PlayArrowFilled
                    width={24}
                    color={
                      hasNoSong
                        ? theme.colors.neutralW50
                        : theme.colors.neutralW20
                    }
                    onClick={handleTogglePlay}
                  />
                )}
              </PlayerIconWrapper>
            </IconContainer>
          </PlayOptionsWrapper>
        ) : (
          <>
            <PlayerDetailsWrapper>
              <PlayOptionsWrapper $isDesktop={isDesktop}>
                <IconContainer>
                  <SkipPreviousIcon
                    $hasNoSong={hasNoSong}
                    $isFirstSong={isFirstSong}
                  >
                    <SkipPreviousFilled
                      width={24}
                      color={
                        isFirstSong || hasNoSong
                          ? theme.colors.neutralW40
                          : theme.colors.neutralW100
                      }
                      onClick={handleSkipBack}
                    />
                  </SkipPreviousIcon>
                </IconContainer>
                <IconContainer>
                  <PlayerIconWrapper $hasNoSong={hasNoSong}>
                    {isPlaying && !hasNoSong ? (
                      <PauseFilled
                        width={24}
                        color={theme.colors.neutralW20}
                        onClick={handleTogglePlay}
                      />
                    ) : (
                      <PlayArrowFilled
                        width={24}
                        color={
                          hasNoSong
                            ? theme.colors.neutralW50
                            : theme.colors.neutralW20
                        }
                        onClick={handleTogglePlay}
                      />
                    )}
                  </PlayerIconWrapper>
                </IconContainer>
                <IconContainer>
                  <SkipNextIcon $hasNoSong={hasNoSong} $isLastSong={isLastSong}>
                    <SkipNextFilled
                      width={24}
                      color={
                        isLastSong || hasNoSong
                          ? theme.colors.neutralW40
                          : theme.colors.neutralW100
                      }
                      onClick={handleSkipFwd}
                      disabled={isLastSong || hasNoSong}
                    />
                  </SkipNextIcon>
                </IconContainer>
              </PlayOptionsWrapper>
              <DurationWrapper $hasNoSong={hasNoSong}>
                {hasNoSong ? '-:--' : stringHelpers.formatTime(currentTime)}
                <Wrap>/</Wrap>
                {hasNoSong
                  ? '-:--'
                  : dateHelpers.getStringFromSeconds(displayDuration)}
              </DurationWrapper>
            </PlayerDetailsWrapper>
            <OptionsWrapper $isDesktop={isDesktop}>
              {isAutoplayGateReady &&
                !isAutoplayToggleInQueue &&
                (hasNoSong ? (
                  <ToggleContainer>
                    <Toggle
                      id={`${autoPlayToggleId}-disabled`}
                      checked={autoPlay}
                      onChange={handleToggleAutoPlay}
                      disabled={true}
                      setting="player"
                      displayIcon={true}
                      containerStyle={{
                        display: 'flex',
                        alignItems: 'center',
                        boxShadow: 'none'
                      }}
                      labelStyle={{ width: 37, height: 20, boxShadow: 'none' }}
                    />
                  </ToggleContainer>
                ) : (
                  <Tonality.Tooltip
                    mode="dark"
                    placement="top"
                    content={
                      autoPlay
                        ? t('general.turnoff-autoplay')
                        : t('general.turnon-autoplay')
                    }
                    containerStyles={{ padding: '4px 8px' }}
                  >
                    <ToggleContainer>
                      <Toggle
                        id={autoPlayToggleId}
                        checked={autoPlay}
                        onChange={handleToggleAutoPlay}
                        setting="player"
                        displayIcon={true}
                        containerStyle={{
                          display: 'flex',
                          alignItems: 'center',
                          boxShadow: 'none'
                        }}
                        labelStyle={{
                          width: 37,
                          height: 20,
                          boxShadow: 'none'
                        }}
                      />
                    </ToggleContainer>
                  </Tonality.Tooltip>
                ))}

              {hasNoSong ? (
                <IconWrapper $hasNoSong={hasNoSong}>
                  <Info
                    width={21}
                    color={theme.colors.neutralW40}
                    onClick={onInfoReleaseClick}
                  />
                </IconWrapper>
              ) : (
                <Tonality.Tooltip
                  mode="dark"
                  placement="top"
                  content={t('general.release-information')}
                  containerStyles={{ padding: '4px 8px' }}
                >
                  <IconWrapper $hasNoSong={hasNoSong}>
                    <Info
                      width={21}
                      color={theme.colors.neutralW100}
                      onClick={onInfoReleaseClick}
                    />
                  </IconWrapper>
                </Tonality.Tooltip>
              )}

              {hasNoSong ? (
                <IconWrapper $hasNoSong={hasNoSong}>
                  <TrackPlaylistDropdown track={selectedSong} disabled={true} />
                </IconWrapper>
              ) : (
                <Tonality.Tooltip
                  mode="dark"
                  placement="top"
                  content={t('playlist.add-to-playlist')}
                  containerStyles={{ padding: '4px 8px' }}
                >
                  <IconWrapper $hasNoSong={hasNoSong}>
                    <TrackPlaylistDropdown
                      track={selectedSong}
                      disabled={false}
                    />
                  </IconWrapper>
                </Tonality.Tooltip>
              )}

              <VolumeSliderWrapper>
                <VolumeSlider
                  value={isMuted ? 0 : localVolume}
                  min={0}
                  max={100}
                  muted={isMuted}
                  onChange={handleVolumeChange}
                  inputStyle={{ width: isDesktop ? '' : '90px' }}
                  iconStyle={{ width: '18px', height: '24px' }}
                  onVolumeIconClick={handleVolumeIconClick}
                />
              </VolumeSliderWrapper>
            </OptionsWrapper>
          </>
        )}
      </PlaybarWrapper>
      {isPlaybarExpanded && <ExpandedPlaybar toggleExpanded={togglePlaybar} />}{' '}
    </>
  )
}

export default Playbar
